import axios from "@/services/axios";
import {
  QueryClient,
  QueryKey,
  UseMutationOptions,
  useMutation as useOriginalMutation,
  useQuery as useOriginalQuery,
  useInfiniteQuery as useOriginalInfiniteQuery,
  UseQueryOptions,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { AxiosRequestConfig } from "axios";
import fileDownload from "js-file-download";
import useSWR from "swr";
import { API_URL } from "../constants";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes by default
      cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes by default
      refetchOnWindowFocus: false, // Don't refetch when window regains focus by default
      refetchOnMount: false, // Don't refetch on component mount if data exists by default
    },
  },
});

export const useQuery = <TData = unknown>(
  url: string,
  opts: Omit<UseQueryOptions<TData>, "queryFn"> & {
    config?: AxiosRequestConfig;
  } = {
    queryKey: [],
  },
) => {
  const { config = {}, ...restOpts } = opts;

  return useOriginalQuery<TData>({
    ...restOpts,
    queryFn: () => fetchDataForQuery(url, config),
  });
};

export const useInfiniteQuery = <TData = unknown>(
  url: string,
  opts: Omit<UseInfiniteQueryOptions<TData>, "queryFn"> & {
    config?: AxiosRequestConfig;
  } = {
    queryKey: [],
  },
) => {
  const { config = {}, ...restOpts } = opts;

  return useOriginalInfiniteQuery<TData>({
    ...restOpts,
    queryFn: ({ pageParam = 1 }) => {
      const params = new URLSearchParams(config.params || {});
      params.set("page", pageParam.toString());

      return fetchDataForQuery(url, {
        ...config,
        params: Object.fromEntries(params),
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.current_page < lastPage?.last_page) {
        return lastPage.current_page + 1;
      }
      return undefined;
    },
  });
};

export const fetchData = (
  endpoint: string,
  config: AxiosRequestConfig = {},
): Promise<any> => {
  return axios
    .get(`${API_URL}${endpoint}`, config)
    .then((response) => {
      if (response.data) {
        return response.data;
      }
    })
    .catch((error) => {
      return error;
    });
};

export const fetchDataForQuery = (
  endpoint: string,
  config: AxiosRequestConfig = {},
): Promise<any> => {
  return axios
    .get(`${API_URL}${endpoint}`, config)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      throw error;
    });
};

export const fetchFileDownload = (endpoint, filename) => {
  const config: AxiosRequestConfig = {
    responseType: "blob",
    headers: {
      Accept: "multipart/form-data",
    },
  };

  return axios
    .get(`${API_URL}${endpoint}`, config)
    .then((response) => {
      if (response) {
        fileDownload(response.data, filename);
      }
    })
    .catch((error) => {
      return error;
    });
};

export const postData = (endpoint, data, config) => {
  return axios
    .post(`${API_URL}${endpoint}`, data, config)
    .then((response) => {
      if (response.data) {
        return response.data;
      }
    })
    .catch((error) => {
      return error;
    });
};

const putData = (endpoint, data, config) => {
  return axios
    .put(`${API_URL}${endpoint}`, data, config)
    .then((response) => {
      if (response.data) {
        return response.data;
      }
    })
    .catch((error) => {
      return error;
    });
};

const deleteData = (endpoint, data) => {
  return axios
    .delete(`${API_URL}${endpoint}`, data)
    .then((response) => {
      if (response.data) {
        return response.data;
      }
    })
    .catch((error) => {
      return error;
    });
};

export const useFetch = (endpoint, debug, config = {}) => {
  return useSWR(endpoint, fetchData, config);
};

export const usePost = (endpoint, data, config = {}) => {
  return postData(endpoint, data, config);
};

export const usePut = (endpoint, data, config = {}) => {
  return putData(endpoint, data, config);
};

export const useDelete = (endpoint, data) => {
  return deleteData(endpoint, data);
};

export const useMutation = <TData = unknown, TVariables = any>(
  endpoint: string,
  opts: Omit<UseMutationOptions<TData, unknown, TVariables>, "mutationFn"> & {
    config?: AxiosRequestConfig;
    method?: "post" | "put" | "delete" | "patch";
  } = {},
) => {
  const { config = {}, method = "post", ...restOpts } = opts;

  return useOriginalMutation<TData, unknown, TVariables>({
    ...restOpts,
    mutationFn: (data: TVariables) => {
      switch (method) {
        case "patch":
          return axios.patch(`${API_URL}${endpoint}`, data, config);
        case "put":
          return axios.put(`${API_URL}${endpoint}`, data, config);
        case "delete":
          return axios.delete(`${API_URL}${endpoint}`, config);
        case "post":
        default:
          return axios.post(`${API_URL}${endpoint}`, data, config);
      }
    },
  });
};
